/**
 * AdminProjectReviewPage
 * UC-023 - Admin Project Review for Approval
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, Card } from '../components/ui';
import { ArrowLeft, Send } from 'lucide-react';
import { api } from '../core/api-client';
import toast from 'react-hot-toast';
import { fmt, fmtMoney } from '../utils/format';

interface ProjectDetail {
  projectId: number;
  name: string;
  description: string;
  status: string;
  useCases: Array<{
    projectUseCaseId: number;
    name: string;
    price: number | string;
    mandatory: boolean;
  }>;
}

export function AdminProjectReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [previewLink, setPreviewLink] = useState('');

  useEffect(() => {
    const loadProject = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const response = await api.get<ProjectDetail>(`/projects/${id}`);
        setProject(response);
      } catch (error) {
        console.error('Failed to load project:', error);
        toast.error(t('admin.projects.review.loadError'));
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [id, t]);

  const handleSubmit = async () => {
    if (!previewLink.trim()) {
      toast.error(t('admin.projects.review.requiredFields'));
      return;
    }

    // Basic URL validation
    const urlPattern = /^https?:\/\/.+/;
    if (!urlPattern.test(previewLink)) {
      toast.error(t('admin.projects.review.invalidUrl'));
      return;
    }

    try {
      setSubmitting(true);

      await api.patch(`/projects/${id}/approval`, {
        status: 'AppPreviewFeedback',
        preview_link: previewLink,
      });

      toast.success(t('admin.projects.review.approveSuccess'));
      navigate(`/admin/projects/${id}`);
    } catch (error) {
      console.error('Failed to approve project:', error);
      toast.error(t('admin.projects.review.approveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(`/admin/projects/${id}`);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card>
          <div className="text-center py-8">
            <p className="text-gray-600">{t('common.loading')}</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card>
          <div className="text-center py-8">
            <p className="text-gray-600">{t('admin.projects.review.notFound')}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              onClick={handleBack}
              className="bg-gray-600 hover:bg-gray-700 text-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.back')}
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('admin.projects.review.title')}
            </h1>
          </div>
        </div>

        <div className="grid gap-8">
          {/* Project Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {t('admin.projects.review.projectInfo')}
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('admin.projects.review.projectName')}
                </label>
                <p className="text-gray-900 font-medium">{project.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('admin.projects.review.description')}
                </label>
                <p className="text-gray-700">{project.description}</p>
              </div>
            </div>
          </div>

          {/* Mandatory Use Cases */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {t('admin.projects.review.mandatoryUseCases')}
            </h2>
            <div className="space-y-3">
              {project.useCases.filter(uc => uc.mandatory).map((useCase) => (
                <div
                  key={useCase.projectUseCaseId}
                  className="flex justify-between items-center p-3 border border-gray-200 rounded-lg bg-gray-50"
                >
                  <div>
                    <p className="font-medium text-gray-900">{useCase.name}</p>
                    <p className="text-sm text-gray-600">
                      {t('admin.projects.review.price')}: {fmtMoney(useCase.price, 2)}
                    </p>
                  </div>
                  <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                    {t('admin.projects.review.mandatory')}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Approval Form */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {t('admin.projects.review.approvalForm')}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.projects.review.previewLink')} *
                </label>
                <Input
                  type="url"
                  value={previewLink}
                  onChange={(e) => setPreviewLink(e.target.value)}
                  placeholder="https://example.com/preview"
                  className="w-full"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('admin.projects.review.previewLinkHelp')}
                </p>
              </div>

            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4 border-t">
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
            >
              <Send className="h-4 w-4" />
              {submitting
                ? t('admin.projects.review.submitting')
                : t('admin.projects.review.approveProject')
              }
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default AdminProjectReviewPage;